import { isEqual } from "lodash";

import { SignalFormState, SignalHookFormState, SignalHookScopeType } from "./types";

import { SignalBinding, SignalBindingPayload, Signal as SignalRecord } from "@/queries/signals";

export const DEFAULT_SAMPLE_RATE = 0.5;
export const MIN_SAMPLE_RATE = 0.01;

export const normalizeSampleRate = (sampleRate: number): number =>
  Math.min(1, Math.max(MIN_SAMPLE_RATE, sampleRate));

export const createEmptyHook = (
  scopeType: SignalHookScopeType = SignalHookScopeType.ALL_REQUESTS,
): SignalHookFormState => ({
  id: crypto.randomUUID(),
  bindingId: null,
  scopeType,
  sampleRate: DEFAULT_SAMPLE_RATE,
  labelNames: [],
  promptId: undefined,
  promptName: undefined,
  promptFolderId: null,
  versionNumber: null,
  versionLabel: null,
  variantType: null,
});

export const bindingToHook = (binding: SignalBinding): SignalHookFormState => {
  if (
    binding.prompt_id === null &&
    binding.label_names === null &&
    binding.version_numbers === null
  ) {
    return {
      id: crypto.randomUUID(),
      bindingId: binding.id,
      scopeType: SignalHookScopeType.ALL_REQUESTS,
      sampleRate: normalizeSampleRate(binding.sample_rate),
      labelNames: [],
      promptId: undefined,
      promptName: undefined,
      promptFolderId: null,
      versionNumber: null,
      versionLabel: null,
      variantType: null,
    };
  }

  if (binding.prompt_id === null && binding.label_names !== null) {
    return {
      id: crypto.randomUUID(),
      bindingId: binding.id,
      scopeType: SignalHookScopeType.PROMPT_LABEL,
      sampleRate: normalizeSampleRate(binding.sample_rate),
      labelNames: binding.label_names,
      promptId: undefined,
      promptName: undefined,
      promptFolderId: null,
      versionNumber: null,
      versionLabel: null,
      variantType: null,
    };
  }

  if (binding.version_numbers !== null) {
    return {
      id: crypto.randomUUID(),
      bindingId: binding.id,
      scopeType: SignalHookScopeType.SPECIFIC_PROMPT,
      sampleRate: normalizeSampleRate(binding.sample_rate),
      labelNames: [],
      promptId: binding.prompt_id ?? undefined,
      promptName: binding.prompt_name ?? undefined,
      promptFolderId: null,
      versionNumber: binding.version_numbers[0] ?? null,
      versionLabel: null,
      variantType: "version",
    };
  }

  if (binding.label_names !== null) {
    return {
      id: crypto.randomUUID(),
      bindingId: binding.id,
      scopeType: SignalHookScopeType.SPECIFIC_PROMPT,
      sampleRate: normalizeSampleRate(binding.sample_rate),
      labelNames: binding.label_names,
      promptId: binding.prompt_id ?? undefined,
      promptName: binding.prompt_name ?? undefined,
      promptFolderId: null,
      versionNumber: null,
      versionLabel: binding.label_names[0] ?? null,
      variantType: "label",
    };
  }

  return {
    id: crypto.randomUUID(),
    bindingId: binding.id,
    scopeType: SignalHookScopeType.SPECIFIC_PROMPT,
    sampleRate: normalizeSampleRate(binding.sample_rate),
    labelNames: [],
    promptId: binding.prompt_id ?? undefined,
    promptName: binding.prompt_name ?? undefined,
    promptFolderId: null,
    versionNumber: null,
    versionLabel: null,
    variantType: null,
  };
};

export const signalToFormState = (signal: SignalRecord) => ({
  name: signal.name,
  prompt: signal.prompt,
  enabled: signal.enabled,
  hooks: signal.bindings.length > 0 ? signal.bindings.map(bindingToHook) : [createEmptyHook()],
});

export const hookToBindingPayload = (hook: SignalHookFormState): SignalBindingPayload | null => {
  const id = hook.bindingId ?? undefined;

  if (hook.scopeType === SignalHookScopeType.ALL_REQUESTS) {
    return {
      id,
      prompt_id: null,
      sample_rate: hook.sampleRate,
      version_numbers: null,
      label_names: null,
    };
  }

  if (hook.scopeType === SignalHookScopeType.PROMPT_LABEL) {
    if (hook.labelNames.length === 0) {
      return null;
    }

    return {
      id,
      prompt_id: null,
      sample_rate: hook.sampleRate,
      version_numbers: null,
      label_names: hook.labelNames,
    };
  }

  if (!hook.promptId) {
    return null;
  }

  if (hook.variantType === "version" && typeof hook.versionNumber === "number") {
    return {
      id,
      prompt_id: hook.promptId,
      sample_rate: hook.sampleRate,
      version_numbers: [hook.versionNumber],
      label_names: null,
    };
  }

  if (hook.variantType === "label" && hook.versionLabel) {
    return {
      id,
      prompt_id: hook.promptId,
      sample_rate: hook.sampleRate,
      version_numbers: null,
      label_names: [hook.versionLabel],
    };
  }

  return {
    id,
    prompt_id: hook.promptId,
    sample_rate: hook.sampleRate,
    version_numbers: null,
    label_names: null,
  };
};

export const hooksToBindingPayloads = (hooks: SignalHookFormState[]): SignalBindingPayload[] =>
  hooks
    .map(hookToBindingPayload)
    .filter((binding): binding is SignalBindingPayload => binding !== null);

const toBindingPayload = ({
  signal_id: _signalId,
  prompt_name: _promptName,
  ...rest
}: SignalBinding): SignalBindingPayload => rest;

const sortBindingsForComparison = (bindings: SignalBindingPayload[]) =>
  [...bindings].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

export const haveBindingsChanged = (
  original: SignalBinding[],
  next: SignalBindingPayload[],
): boolean =>
  !isEqual(
    sortBindingsForComparison(original.map(toBindingPayload)),
    sortBindingsForComparison(next),
  );

export const isHookComplete = (hook: SignalHookFormState): boolean =>
  hookToBindingPayload(hook) !== null;

type HookCoverage =
  | { type: "all" }
  | { type: "workspace_labels"; labels: string[] }
  | { type: "prompt_all"; promptId: number }
  | { type: "prompt_versions"; promptId: number; versions: number[] }
  | { type: "prompt_labels"; promptId: number; labels: string[] };

const getHookCoverage = (hook: SignalHookFormState): HookCoverage | null => {
  const binding = hookToBindingPayload(hook);
  if (!binding) {
    return null;
  }

  if (
    binding.prompt_id === null &&
    binding.label_names === null &&
    binding.version_numbers === null
  ) {
    return { type: "all" };
  }

  if (binding.prompt_id === null && binding.label_names) {
    return { type: "workspace_labels", labels: binding.label_names };
  }

  if (binding.prompt_id === null) {
    return null;
  }

  if (binding.version_numbers) {
    return {
      type: "prompt_versions",
      promptId: binding.prompt_id,
      versions: binding.version_numbers,
    };
  }

  if (binding.label_names) {
    return {
      type: "prompt_labels",
      promptId: binding.prompt_id,
      labels: binding.label_names,
    };
  }

  return { type: "prompt_all", promptId: binding.prompt_id };
};

const isCoverageCoveredByOthers = (target: HookCoverage, others: HookCoverage[]): boolean => {
  if (others.length === 0) {
    return false;
  }

  if (others.some(other => other.type === "all")) {
    return true;
  }

  if (target.type === "all") {
    return others.some(other => other.type === "all");
  }

  if (target.type === "workspace_labels") {
    const coveredLabels = new Set<string>();
    for (const other of others) {
      if (other.type === "workspace_labels") {
        for (const label of other.labels) {
          coveredLabels.add(label);
        }
      }
    }
    return target.labels.every(label => coveredLabels.has(label));
  }

  if (target.type === "prompt_all") {
    return others.some(other => other.type === "prompt_all" && other.promptId === target.promptId);
  }

  if (target.type === "prompt_versions") {
    if (others.some(other => other.type === "prompt_all" && other.promptId === target.promptId)) {
      return true;
    }

    const coveredVersions = new Set<number>();
    for (const other of others) {
      if (other.type === "prompt_versions" && other.promptId === target.promptId) {
        for (const version of other.versions) {
          coveredVersions.add(version);
        }
      }
    }
    return target.versions.every(version => coveredVersions.has(version));
  }

  if (others.some(other => other.type === "prompt_all" && other.promptId === target.promptId)) {
    return true;
  }

  const coveredLabels = new Set<string>();
  for (const other of others) {
    if (other.type === "workspace_labels") {
      for (const label of other.labels) {
        coveredLabels.add(label);
      }
    }
    if (other.type === "prompt_labels" && other.promptId === target.promptId) {
      for (const label of other.labels) {
        coveredLabels.add(label);
      }
    }
  }
  return target.labels.every(label => coveredLabels.has(label));
};

/**
 * Hooks that don't expand coverage beyond the others.
 * Exact duplicates, subsets (prod when prod+dev exists), and combinations
 * already covered by separate hooks (prod+dev when prod and dev exist).
 */
export const getRedundantHookIds = (hooks: SignalHookFormState[]): Set<string> => {
  const entries = hooks
    .map(hook => ({ id: hook.id, coverage: getHookCoverage(hook) }))
    .filter((entry): entry is { id: string; coverage: HookCoverage } => entry.coverage !== null);

  const redundantIds = new Set<string>();
  const kept: { id: string; coverage: HookCoverage }[] = [];

  for (const entry of entries) {
    if (
      isCoverageCoveredByOthers(
        entry.coverage,
        kept.map(keptEntry => keptEntry.coverage),
      )
    ) {
      redundantIds.add(entry.id);
      continue;
    }
    kept.push(entry);
  }

  for (const entry of kept) {
    const others = kept
      .filter(keptEntry => keptEntry.id !== entry.id)
      .map(keptEntry => keptEntry.coverage);
    if (isCoverageCoveredByOthers(entry.coverage, others)) {
      redundantIds.add(entry.id);
    }
  }

  return redundantIds;
};

export const validateHooks = (hooks: SignalHookFormState[]): string | null => {
  const bindings = hooksToBindingPayloads(hooks);

  if (bindings.length === 0) {
    return "Add at least one hook.";
  }

  if (hooks.some(hook => hook.scopeType === SignalHookScopeType.ALL_REQUESTS) && hooks.length > 1) {
    return "All requests cannot be combined with other hooks.";
  }

  if (getRedundantHookIds(hooks).size > 0) {
    return "Remove or change redundant hooks to save.";
  }

  return null;
};

export const getSignalFormSaveDisabledReason = (formState: SignalFormState): string | null => {
  if (!formState.name.trim() || !formState.prompt.trim()) {
    return "Fill in all required fields to save";
  }

  if (!formState.hooks.every(isHookComplete)) {
    return "Fill in all required fields to save";
  }

  return validateHooks(formState.hooks);
};

export const isSignalFormValid = (formState: SignalFormState): boolean =>
  getSignalFormSaveDisabledReason(formState) === null;

export const getScopeTypeLabel = (scopeType: SignalHookScopeType): string => {
  switch (scopeType) {
    case SignalHookScopeType.ALL_REQUESTS:
      return "All requests";
    case SignalHookScopeType.PROMPT_LABEL:
      return "Prompt template label";
    case SignalHookScopeType.SPECIFIC_PROMPT:
      return "Specific prompt template";
    default:
      return scopeType;
  }
};

export const formatSampleRatePercent = (sampleRate: number): string =>
  `${Math.round(sampleRate * 100)}%`;

export const getHookSummary = (hook: SignalHookFormState, index: number): string => {
  const sampleRate = formatSampleRatePercent(hook.sampleRate);

  if (hook.scopeType === SignalHookScopeType.PROMPT_LABEL) {
    const labels =
      hook.labelNames.length > 0 ? hook.labelNames.join(", ") : getScopeTypeLabel(hook.scopeType);
    return `Hook ${index + 1} · ${labels} · ${sampleRate}`;
  }

  if (hook.scopeType === SignalHookScopeType.SPECIFIC_PROMPT && hook.promptName) {
    let promptPart = hook.promptName;
    if (hook.variantType === "version" && typeof hook.versionNumber === "number") {
      promptPart = `${promptPart} v${hook.versionNumber}`;
    } else if (hook.variantType === "label" && hook.versionLabel) {
      promptPart = `${promptPart} (${hook.versionLabel})`;
    }

    return `Hook ${index + 1} · ${promptPart} · ${sampleRate}`;
  }

  return `Hook ${index + 1} · ${getScopeTypeLabel(hook.scopeType)} · ${sampleRate}`;
};
